import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';
import type { Competitor } from '../../types/database';

type OnboardingContext = {
  canExitOnboarding?: boolean;
  previousWorkspaceId?: string | null;
  createdWorkspaceId?: string | null;
};

export function OnboardingStep2() {
  const MAX_COMPETITORS = 10;
  const navigate = useNavigate();
  const { workspace, user, profile, fetchProfile } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [newCompetitorUrl, setNewCompetitorUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isSearching, setIsSearching] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [onboardingContext, setOnboardingContext] = useState<OnboardingContext | null>(null);
  const findInFlight = useRef(false);

  useEffect(() => {
    const rawContext = sessionStorage.getItem('lima_onboarding_context');
    if (!rawContext) {
      setOnboardingContext(null);
      return;
    }

    try {
      setOnboardingContext(JSON.parse(rawContext) as OnboardingContext);
    } catch {
      setOnboardingContext(null);
    }
  }, []);

  const { data: competitors = [], isLoading } = useQuery({
    queryKey: ['competitors', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('competitors')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as Competitor[];
    },
    enabled: !!workspace?.id,
  });

  useEffect(() => {
    if (!workspace?.brand_url) {
      navigate('/onboarding/step-1', { replace: true });
      return;
    }

    if (competitors.length === 0 && isSearching && !findInFlight.current) {
      findCompetitors();
    } else if (competitors.length > 0) {
      setIsSearching(false);
    }
  }, [workspace?.brand_url, competitors.length]);

  const findCompetitors = async () => {
    if (!workspace?.id || !workspace?.brand_url || findInFlight.current) return;
    findInFlight.current = true;
    setIsSearching(true);

    try {
      const { error } = await supabase.functions.invoke('find-competitors', {
        body: {
          workspace_id: workspace.id,
          brand_url: workspace.brand_url,
        },
      });

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['competitors', workspace.id] });
    } catch (error: any) {
      console.error('Error finding competitors:', error);
      const { data: existingCompetitors } = await supabase
        .from('competitors')
        .select('id')
        .eq('workspace_id', workspace.id)
        .limit(1);

      if (!existingCompetitors || existingCompetitors.length === 0) {
        toast.error('Could not auto-find competitors. Add them manually below.');
      }
    } finally {
      setIsSearching(false);
      findInFlight.current = false;
    }
  };

  const extractDomainName = (domain: string): string => {
    const clean = domain.replace(/^www\./, '');
    const parts = clean.split('.');
    return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  };

  const handleAddCompetitor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCompetitorUrl.trim() || !workspace?.id) return;

    setIsAdding(true);
    try {
      if (competitors.length >= MAX_COMPETITORS) {
        toast.error(`You can track up to ${MAX_COMPETITORS} competitors`);
        return;
      }

      let cleanUrl = newCompetitorUrl.trim().toLowerCase();
      cleanUrl = cleanUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');

      if (!cleanUrl.includes('.')) {
        toast.error('Please enter a valid domain');
        setIsAdding(false);
        return;
      }

      const exists = competitors.some(c => c.domain === cleanUrl);
      if (exists) {
        toast.error('This competitor is already in your list');
        setIsAdding(false);
        return;
      }

      const { error } = await supabase.from('competitors').insert({
        workspace_id: workspace.id,
        name: extractDomainName(cleanUrl),
        domain: cleanUrl,
        favicon_url: `https://www.google.com/s2/favicons?domain=${cleanUrl}&sz=64`,
      });

      if (error) throw error;

      setNewCompetitorUrl('');
      queryClient.invalidateQueries({ queryKey: ['competitors', workspace.id] });
    } catch (error: any) {
      console.error('Error adding competitor:', error);
      toast.error(error.message || 'Failed to add competitor');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveCompetitor = async (id: string) => {
    try {
      const { error } = await supabase.from('competitors').delete().eq('id', id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['competitors', workspace?.id] });
    } catch (error: any) {
      toast.error('Failed to remove competitor');
    }
  };

  const handleContinue = () => {
    navigate('/onboarding/step-3');
  };

  const handleCancel = async () => {
    if (!user || !profile?.workspace_id) return;

    setIsExiting(true);
    try {
      const targetWorkspaceId = onboardingContext?.previousWorkspaceId || profile.workspace_id;
      const createdWorkspaceId = onboardingContext?.createdWorkspaceId;

      const { error } = await supabase
        .from('profiles')
        .update({
          workspace_id: targetWorkspaceId,
          onboarding_completed: true,
        })
        .eq('id', user.id);

      if (error) throw error;

      if (
        createdWorkspaceId &&
        createdWorkspaceId !== targetWorkspaceId &&
        profile.workspace_id === createdWorkspaceId
      ) {
        await supabase.from('workspace_members').delete().eq('workspace_id', createdWorkspaceId);
        await supabase.from('workspaces').delete().eq('id', createdWorkspaceId);
      }

      sessionStorage.removeItem('lima_onboarding_context');
      await fetchProfile(user.id);
      queryClient.invalidateQueries({ queryKey: ['userWorkspaces'] });
      navigate('/overview', { replace: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to exit onboarding');
    } finally {
      setIsExiting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="app-wrapper">
        <div id="no-saved-ads" className="onboarding-wrapper">
          <div className="intro-wrapper">
            {onboardingContext?.canExitOnboarding && (
              <>
                <button type="button" className="link-style" onClick={handleCancel} disabled={isExiting}>
                  Cancel
                </button>
                <div className="spacer-small"></div>
              </>
            )}
            <div className="text-size-large text-weight-semibold text-colour-dark-brown">
              {isSearching && competitors.length === 0
                ? "We're searching for competitors"
                : "We've found some competitors"}
            </div>
            <div className="spacer-xxtiny"></div>
            <div className="text-size-regular text-colour-alt-grey align-center">
              {isSearching && competitors.length === 0
                ? "This may take a few seconds..."
                : "Add or remove competitors below (you can add more later)"}
            </div>
            <div className="plus-btn"></div>
            <div className="spacer-small"></div>

            <div className="card is-onboarding">
              <div className="box-header-row">
                <div className="text-size-regular text-weight-semibold">
                  Competitor list{' '}
                  {competitors.length > 0 && (
                    <span className="number-pill">{competitors.length}</span>
                  )}
                </div>
              </div>

              <div className="list-wrapper">
                {(isSearching || isLoading) && competitors.length === 0 ? (
                  <div className="step-loader is-shimmer">
                    <div className="search-icon-embed">
                      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m21 21-4.34-4.34" />
                        <circle cx="11" cy="11" r="8" />
                      </svg>
                    </div>
                    <div className="spacer-tiny"></div>
                    <div className="text-size-regular text-weight-medium text-colour-light-grey">
                      Finding competitors...
                    </div>
                  </div>
                ) : (
                  competitors.map((competitor, index) => (
                    <div
                      key={competitor.id}
                      className={`competitor-row${index === 0 ? ' is-top' : ''}`}
                    >
                      <div className="competitor-left">
                        <div
                          className="site-icon"
                          style={
                            competitor.favicon_url
                              ? { backgroundImage: `url(${competitor.favicon_url})` }
                              : undefined
                          }
                        />
                        <div>
                          <div className="text-weight-semibold text-size-regular">{competitor.name}</div>
                          <div className="text-size-small text-colour-alt-grey">{competitor.domain}</div>
                        </div>
                      </div>
                      <div
                        className="remove-icon-holder"
                        onClick={() => handleRemoveCompetitor(competitor.id)}
                      >
                        <div className="embed-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="competitor-form">
                <form onSubmit={handleAddCompetitor} className="url-form">
                  <label htmlFor="competitor-url" className="form_label has-left-spacing">
                    Add competitor <span className="text-colour-alt-grey">(optional - can do later)</span>
                  </label>
                  <input
                    className="form_input is-url-input is-white has-inline-add-button w-input"
                    maxLength={256}
                    placeholder="e.g. google.com"
                    type="text"
                    id="competitor-url"
                    value={newCompetitorUrl}
                    onChange={(e) => setNewCompetitorUrl(e.target.value)}
                    disabled={isAdding}
                  />
                  <button
                    type="submit"
                    className={`button is-small is-form-button w-button${newCompetitorUrl.trim() ? '' : ' is-lighter'}`}
                    disabled={isAdding || !newCompetitorUrl.trim()}
                  >
                    Add
                  </button>
                </form>
              </div>
            </div>

            <div className="spacer-small"></div>
            <div className="align-content-middle">
              <button
                onClick={handleContinue}
                className="button w-button"
                disabled={competitors.length === 0}
              >
                Continue to final step
              </button>
            </div>
          </div>
        </div>
        <div className="gradient-style"></div>
      </div>
    </div>
  );
}
